import { gql } from '@apollo/client';
import { transformToMap } from '../utils/TranslationUtil';
import { generateApolloClient } from '../utils/GqlUtil';
import { getCookie } from '../utils/CookieUtil';

require('isomorphic-fetch');

const apolloOptions = {};
if (typeof window === 'object' && window.ha && window.ha.siteName) {
    // Use both site and siteName because different tools use one or the other.
    apolloOptions.queryParams = {
        siteName: window.ha.siteName,
        site: window.ha.siteName
    };
}
let localClient = generateApolloClient(apolloOptions);

// This was added to allow us to more easily mock
// the client so we can get the unit tests working
// again.
function overrideClient(client) {
    localClient = client;
}

function getClient() {
    return localClient;
}

function getData(query, variables, start, success, mutate, fail, noCache) {
    return (dispatch) => {
        dispatch(start());
        const client = getClient();
        const payload = {
            query,
            variables
        };
        if (noCache) payload.fetchPolicy = 'no-cache';
        return client.query(payload).then(
            (result) => mutate(result)
        ).then((mappedData) => dispatch(success(mappedData))).catch((error) => dispatch(fail(error)));
    };
}

function setData(mutation, variables, start, success, mutate, fail) {
    return (dispatch) => {
        dispatch(start());
        const client = getClient();
        return client.mutate({
            mutation,
            variables
        }).then(
            (result) => mutate(result)
        ).then((mappedData) => dispatch(success(mappedData))).catch((error) => dispatch(fail(error)));
    };
}

export async function retrieveData(query, variables) {
    const client = getClient();
    const result = await client.query({
        query,
        variables
    });
    return result;
}
export async function retrieveDataNoCache(query, variables) {
    const client = getClient();
    const result = await client.query({
        query,
        variables,
        fetchPolicy: 'no-cache'
    });
    return result;
}
const mutateLocaleData = (result) => {
    const translations = transformToMap(result.data.getLocaleData.translations);
    const { vrboOwnerAuthUrl, vrboGenericAuthUrl } = result.data.getLocaleData;
    return (
        {
            translations,
            vrboOwnerAuthUrl,
            vrboGenericAuthUrl
        }
    );
};

function getLocaleData(locale, start, success, fail) {
    const query = gql`
    query GetLocaleData($locale: String!) {
        getLocaleData(locale: $locale) {
            translations {
                key,
                value
            }
            vrboOwnerAuthUrl,
            vrboGenericAuthUrl
        }
    }`;
    // eslint-disable-next-line object-shorthand
    const variables = { locale: locale };
    return getData(query, variables, start, success, mutateLocaleData, fail);
}

function getEstimate(propertyInfo, locale, start, success, fail) {
    const query = gql`
    query Estimate($propertyInfo: PropertyInfo!, $locale: String!) {
        getEstimate(propertyInfo: $propertyInfo, locale: $locale) {
            isFallback, fallbackSource, estimateUpperBound, beds, estimateType, conversionRate, currencyCode
        }
    }`;
    // eslint-disable-next-line object-shorthand
    const {
        lat, lng, bedroomNum, bathroomNum
    } = propertyInfo;
    const variables = {
        propertyInfo: {
            lat, lng, bedroomNum, bathroomNum
        },
        locale
    };
    const mutate = (result) => result.data.getEstimate;
    return getData(query, variables, start, success, mutate, fail);
}

function updateAccount(locale, fingerprint, trustPayload, start, success, fail) {
    const mutation = gql`
    mutation UpdateContact($contact: UpdatePartnerContactInput!) {
        updateContact(contact: $contact) {
            type,
            vrboContact {
                autoAuthUrl,
                userPublicUuid,
                newSupplier
            }
        }
    }
    `;
    // eslint-disable-next-line object-shorthand
    const contact = {
        type: 'FRBO',
        phoneCountryCode: '',
        phoneNumber: '',
        password: '',
        trustPayload,
        fingerprint,
        locale,
        duaid: getCookie('DUAID'),
        source: 'UPGRADE ACCOUNT'
    };
    const variables = { contact };
    const mutate = (result) => result.data.updateContact;
    return setData(mutation, variables, start, success, mutate, fail);
}

function sendEloquaData({
    expediaId,
    link,
    address
}, start, success, fail) {
    const mutation = gql`
    mutation EloquaCompetitorLinkCapture($data: EloquaInput!) {
        eloquaCompetitorLinkCapture(data: $data) {
           statusCode,
           statusMessage
        }
    }
    `;

    const data = {
        expediaId,
        link,
        address
    };
    const variables = { data };
    const mutate = (result) => result.data.eloquaCompetitorLinkCapture;
    return setData(mutation, variables, start, success, mutate, fail);
}

function getFooterLinks(referrerBrand, locale, start, success, fail) {
    const query = gql`
    query FooterLinks($referrerBrand: String!, $locale: String!) {
        footerLinks(referrerBrand: $referrerBrand, locale: $locale) {
            logo {
                image,
                link,
                text,
                altText
            },
            footerHtml,
            social{
                link,
                text
            }
        }
    }`;

    const variables = {
        referrerBrand,
        locale
    };
    const mutate = (result) => result.data.footerLinks;
    return getData(query, variables, start, success, mutate, fail);
}

function validateEmail(
    email,
    isDoubledValidation,
    isServerEmailValidationEnabled,
    start,
    success,
    fail
) {
    const query = gql`
    query ValidationResult($email: String!, $isDoubledValidation: Boolean!, $isServerEmailValidationEnabled: Boolean!) {
        validateEmail(email: $email, isDoubledValidation: $isDoubledValidation, isServerEmailValidationEnabled: $isServerEmailValidationEnabled) {
            emailValidationResult
        }
    }`;
    const variables = { email, isDoubledValidation, isServerEmailValidationEnabled };
    const mutate = (result) => result.data.validateEmail;
    return getData(query, variables, start, success, mutate, fail, true);
}
async function validateEmailSync(
    email,
    isDoubledValidation,
    isServerEmailValidationEnabled
) {
    const query = gql`
    query ValidationResult($email: String!, $isDoubledValidation: Boolean!, $isServerEmailValidationEnabled: Boolean!) {
        validateEmail(email: $email, isDoubledValidation: $isDoubledValidation, isServerEmailValidationEnabled: $isServerEmailValidationEnabled) {
            emailValidationResult
        }
    }`;
    const variables = { email, isDoubledValidation, isServerEmailValidationEnabled };

    const validateEmailResult = await retrieveData(query, variables);
    return validateEmailResult;
}

function validatePhone(phoneNumber, start, success, fail) {
    const query = gql`
    query ($phoneNumber: String!) {
        isValidPhoneNumber(phoneNumber: $phoneNumber)
    }`;
    // eslint-disable-next-line object-shorthand
    const variables = { phoneNumber: phoneNumber };
    const mutate = (result) => result.data.isValidPhoneNumber;
    return getData(query, variables, start, success, mutate, fail);
}

function fetchCurrencyForCountryCode(countryCode, start, success, fail) {
    const query = gql`
    query ($countryCode: String!) {
        fetchCurrency(countryCode: $countryCode)
    }`;
    // eslint-disable-next-line object-shorthand
    const variables = { countryCode: countryCode };
    const mutate = (result) => {
        const v = result.data.fetchCurrency;
        return v || ['USD']; // if we cannot find a currency use USD.
    };
    return getData(query, variables, start, success, mutate, fail);
}

function validateWithGeoCoder({
    address, latLng, latLngFallback, propertyName, pathSource
}, start, success, fail) {
    const query = gql`
        query GeoCoderValidation($address: Address!, $latLng: LatLngInput, $latLngFallback: Boolean, $propertyName: String, $pathSource: String) {
            isValidByGeoCoder(address: $address, latLng: $latLng, latLngFallback: $latLngFallback, propertyName: $propertyName, pathSource: $pathSource){
                value,
                errorList
            }
        }
    `;
    const variables = {
        address,
        latLng: { lat: latLng.lat, lng: latLng.lng },
        latLngFallback,
        propertyName,
        pathSource
    };
    const mutate = (result) => result.data.isValidByGeoCoder;
    return getData(query, variables, start, success, mutate, fail);
}

function getLatLngGeocoder({
    address, candidateLatLng, propertyName, pathSource, minCoordinates
}, start, success, fail) {
    const query = gql`
        query GeoCoderLatLng($address: Address!, $candidateLatLng: LatLngInput, $propertyName: String, $pathSource: String, $minCoordinates: String) { 
            getLatLng(address: $address, candidateLatLng: $candidateLatLng, propertyName: $propertyName, pathSource: $pathSource, minCoordinates: $minCoordinates) {
                lat,
                lng,
                error,
                threshold
            }
        }
    `;
    const variables = {
        address, candidateLatLng, propertyName, pathSource, minCoordinates
    };
    const mutate = (result) => result.data.getLatLng;
    return getData(query, variables, start, success, mutate, fail);
}

function validateAddressUnitNumber(address, start, success, fail) {
    const query = gql`
        query ValidateUnitNumber($address: Address!) {
            isUnitNumberValid(address: $address) {
                isValid,
                isMissingUnitNumber,
                isValidUnitNumber
            }
        }
    `;
    const variables = { address };
    const mutate = (result) => {
        if (!result.data.isUnitNumberValid.isValid) {
            return {
                number: address.address2,
                missingUnitNumber: result.data.isUnitNumberValid.isMissingUnitNumber,
                unitNumberValid: result.data.isUnitNumberValid.isValidUnitNumber
            };
        }
        return {
            number: address.address2,
            missingUnitNumber: false,
            unitNumberValid: true
        };
    };
    return getData(query, variables, start, success, mutate, fail);
}

export const ENCRYPTION_QUERY = gql`
    query EncryptValue($value: String!) {
        encryptValue(value: $value)
    }
`;

export const GET_NEXT_PAGE_QUERY = gql`
    query getNextPage($propertyId: String!, $additionalData: AdditionalData!) {
        getNext(propertyId: $propertyId, additionalData: $additionalData)
    }
`;

export const ENGLISH_ADDRESS_QUERY = gql`
    query EnglishAddress($placeId: String!) {
        fetchEnglishAddress(placeId: $placeId) {
            address1,
            address2,
            city,
            stateProvince,
            postalCode,
            country
        }
    }
`;

export const LYP_ADDRESS_LOOKUP_QUERY = gql`
    query LypAddressLookup($input: LypAddressLookupInput!) {
        lypAddressLookup(input: $input) {
            found,
            propertyId,
            status,
            propertyName,
            propertyType,
            currency,
            unitCount
        }
    }
`;

export async function getLypPropertyByAddress({
    street,
    city,
    state,
    zip,
    country,
    latitude,
    longitude,
    propertyName,
    websiteUrl,
    placeId
}) {
    const result = await retrieveDataNoCache(LYP_ADDRESS_LOOKUP_QUERY, {
        input: {
            street,
            city,
            state,
            zip,
            country,
            latitude,
            longitude,
            propertyName,
            websiteUrl,
            placeId
        }
    });
    return result.data.lypAddressLookup;
}

export {
    validateWithGeoCoder,
    getLatLngGeocoder,
    validateAddressUnitNumber,
    overrideClient,
    getClient,
    getData,
    getLocaleData,
    getEstimate,
    getFooterLinks,
    updateAccount,
    validateEmail,
    validatePhone,
    fetchCurrencyForCountryCode,
    validateEmailSync,
    sendEloquaData
};
